import { Tab } from "@headlessui/react";
import classNames from "classnames";
import cn from "classnames";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";

import Icon from "~/components/common/icon";
import useSubscriptions from "~/hooks/use-subscriptions";
import Editorials from "~/types/editorials";
import appRoutes from "~/utils/app-routes";

import styles from "./loyalty-tabs.module.scss";

type Props = {
  tabs: Editorials.LoyaltyTabsModel[];
  isBackgroundGradient?: boolean;
};

export default function LoyaltyTabs(props: Props) {
  const { tabs, isBackgroundGradient } = props;
  const router = useRouter();
  const { currentLoyaltyTier } = useSubscriptions();
  const [activeIndex, setActiveIndex] = useState(0);
  const previousIndexRef = useRef<number | undefined>(undefined);
  const tabsRef = useRef<HTMLElement[]>([]);

  const isMyAccountLoyaltyPage = useMemo(() => router.asPath === appRoutes.LOYALTY + "/", [router.asPath]);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    function translateAfterSelect() {
      const parentPos = tabsRef.current[activeIndex]?.parentElement?.getBoundingClientRect();
      const childPos = tabsRef.current[activeIndex]?.getBoundingClientRect();
      if (parentPos && childPos) {
        const relativeOffset = childPos.left - parentPos.left;

        tabsRef.current[activeIndex]?.parentElement?.scrollTo({ left: relativeOffset + 24, behavior: "smooth" });
      }
    }
    if ((previousIndexRef.current == undefined && activeIndex != 0) || previousIndexRef.current != undefined) {
      scrollTimeout = setTimeout(() => {
        translateAfterSelect();
      }, 500);
      previousIndexRef.current = activeIndex;
    }
    return () => clearTimeout(scrollTimeout);
  }, [activeIndex]);

  useEffect(() => {
    if (isMyAccountLoyaltyPage && currentLoyaltyTier?.Name) {
      const tabNames = tabs.map((a) => a.tabName?.toLocaleLowerCase());
      const tierIndex = tabNames.findIndex((name) => name === currentLoyaltyTier.Name.toLocaleLowerCase());
      if (tierIndex > 0) {
        setActiveIndex(tierIndex);
      }
    }
  }, [isMyAccountLoyaltyPage, currentLoyaltyTier?.Name, tabs]);

  return (
    <div className={classNames(styles.container, isMyAccountLoyaltyPage ? styles.myAccountLoyaltyPage : undefined)}>
      <Tab.Group selectedIndex={activeIndex} onChange={setActiveIndex}>
        <Tab.List
          className={classNames(styles.tabList, isMyAccountLoyaltyPage ? styles.myAccountLoyaltyPage : undefined)}
          aria-roledescription="tablist"
        >
          {tabs.map((tab, tabIndex) => (
            <Tab
              key={`tab-${tabIndex}`}
              ref={(ref) => (tabsRef.current[tabIndex] = ref!)}
              className={({ selected }) => classNames(styles.tab, selected ? styles.activeTab : undefined)}
              aria-roledescription="tab"
            >
              {tab.tabName || `Tab ${tabIndex + 1}`}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels>
          {tabs.map((tab, panelIndex) => (
            <Tab.Panel
              key={`panel-${panelIndex}`}
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              aria-roledescription="tabpanel"
            >
              {tab.tabDescription && <p className={styles.description}>{tab.tabDescription}</p>}
              <div className={styles.items}>
                {tab.items.map((item, itemIndex) => (
                  <div
                    key={`item-${panelIndex}-${itemIndex}`}
                    className={cn(styles.itemCard, isBackgroundGradient ? styles.backgroundWhite : undefined)}
                  >
                    <strong className={styles.itemTitle}>{item.title}</strong>
                    {item.iconId && (
                      <div className={styles.iconWrapper}>
                        <Icon name={item.iconId} width={24} height={24} />
                      </div>
                    )}
                    <p className={styles.itemDescription}>{item.description}</p>
                  </div>
                ))}
              </div>
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
